// SAB-644: native App-based location submission (Locance appData interface).
//
// The iOS/Android app is a Capacitor WebView that remote-loads the site. In that
// container Locance's HTML5 collector (window.lshtml5 -> navigator.geolocation)
// cannot produce a resolvable HTML5 session, so a native money action never gets
// a geo session and fails closed. Locance's prescribed native path: gather the
// device location via the OS (Capacitor Geolocation) and POST it directly to the
// appData interface, then reference that session under appSessionId in the
// compliance request (LS_API v4.23 §5.3, §7.1.2).
//
// NATIVE-ONLY. useLocanceGeoCheck.run() calls this only under
// Capacitor.isNativePlatform(); web is completely untouched.

import { Geolocation } from '@capacitor/geolocation';
// SAB-644: CapacitorHttp is used ONLY by submitAppDataLocation (native-only) to POST via the
// native HTTP stack, bypassing the WKWebView CORS wall on the appData host. It is referenced
// nowhere on the web path, and we do NOT enable the global CapacitorHttp fetch patch.
import { CapacitorHttp } from '@capacitor/core';
import { LOCANCE_ORG_ID } from '@/lib/locance';

// appData ingest host. Same host across staging + prod (the appId, not the URL,
// selects the tenant — see below). Overridable for a preview/test pointing at a
// different ingest, but defaults to the first-party subdomain Locance gave us.
const APPDATA_URL =
  (import.meta.env.VITE_LOCANCE_APPDATA_URL as string | undefined) ??
  'https://lsl.sabotagefantasy.com/v4/appdata/';

// Prod Locance org id (lsdp.dp). Used only to cross-check the appId env below.
const PROD_ORG_ID = '34mvnza6';

/**
 * Resolve + validate the appData appId. FAIL LOUD (throw), never a silent
 * fallback: a wrong/blank appId drops every native session with no server-side
 * signal, so we refuse to POST rather than guess.
 *   - unset / empty            -> throw (env not provisioned for this build)
 *   - prod appId on non-prod org, or non-prod appId on prod org -> throw
 *     (the demo appId shipped to prod, or the prod appId on staging, would send
 *     real users' locations to the wrong tenant; the org id already distinguishes
 *     the two, so an appId whose _prod/_demo suffix disagrees with it is a
 *     build-env mismatch we must not paper over).
 */
export function resolveAppDataAppId(): string {
  const appId = (import.meta.env.VITE_LOCANCE_APP_ID as string | undefined)?.trim();
  if (!appId) {
    throw new Error(
      'VITE_LOCANCE_APP_ID is not set. The native appData path requires a provisioned ' +
        'Locance appId; refusing to submit location without it.',
    );
  }
  const isProdAppId = appId.endsWith('_prod');
  const isProdOrg = LOCANCE_ORG_ID === PROD_ORG_ID;
  if (isProdAppId !== isProdOrg) {
    throw new Error(
      `VITE_LOCANCE_APP_ID ("${appId}") does not match VITE_LOCANCE_ORG_ID ("${LOCANCE_ORG_ID}"): ` +
        'a _prod appId must pair with the prod org and a non-prod appId with a non-prod org. ' +
        'Refusing to submit location to a mismatched tenant.',
    );
  }
  return appId;
}

/**
 * Format an epoch-ms instant as ISO-8601 with milliseconds and an EXPLICIT
 * +00:00 offset (not the Z form). Guards a device clock running ahead: a future
 * instant is clamped to `nowMs` so we never submit a timestamp the server reads
 * as impossible.
 */
function toLocanceTimestamp(epochMs: number, nowMs: number): string {
  const clamped = Number.isFinite(epochMs) && epochMs > 0 && epochMs <= nowMs ? epochMs : nowMs;
  // new Date().toISOString() -> "2026-08-06T12:34:56.789Z"; swap the Z for +00:00.
  return new Date(clamped).toISOString().replace(/Z$/, '+00:00');
}

// One collected location source. The appData `locations` array carries multiple
// SOURCES (e.g. GPS vs network), not a time series, so we submit the available
// fix(es) once — not a watch history. `method` is optional in the spec; we OMIT it.
interface AppDataLocation {
  latitude: number;
  longitude: number;
  accuracy: number;
  timestamp: string;
}

export type AppDataBlockKind = 'consent_denied' | 'transient' | 'unavailable';

export interface AppDataSubmitResult {
  ok: boolean;
  statusCode: number | null; // Locance appData response statusCode, when present
  retriable: boolean; // caller may re-run the collector once on a transient miss
  kind?: AppDataBlockKind; // block classification when !ok
  detail?: string; // human-readable reason when !ok
  // SAB-644 gather instrumentation: the appData path calls Capacitor DIRECTLY (not the
  // navigator.geolocation shim), so the shim-based beacon fields are null on it. These let
  // us tell a Locance "No latitude specified" rejection apart from OUR failing to obtain a
  // position before the POST (an empty locations array). Fed to geo_gather_events
  // geo_fix / geo_method / geo_error; accuracy + the sent timestamp ride in `detail`.
  positionObtained: boolean; // did we get a usable fix (valid accuracy) to POST
  geoMethod: string; // native gather method, e.g. 'appData:getCurrentPosition'
  geoError: string | null; // gather-side error when no usable fix was obtained
}

// Native gather method label for the beacon (geo_method column).
const APPDATA_GEO_METHOD = 'appData:getCurrentPosition';

async function ensurePermission(): Promise<'granted' | 'denied' | 'error'> {
  try {
    const perm = await Geolocation.checkPermissions();
    if (perm.location === 'granted') return 'granted';
    if (perm.location === 'denied') return 'denied';
    const req = await Geolocation.requestPermissions({ permissions: ['location'] });
    return req.location === 'granted' ? 'granted' : 'denied';
  } catch {
    return 'error';
  }
}

/**
 * Gather the native device location and submit it to the Locance appData
 * interface for `sessionId`. Returns a structured result the collector maps to
 * the same UI states as the web HTML5 path (denied / transient / unavailable).
 * Never throws for expected outcomes; only a truly unexpected failure propagates.
 */
export async function submitAppDataLocation(sessionId: string): Promise<AppDataSubmitResult> {
  const appId = resolveAppDataAppId(); // throws on unprovisioned/mismatched env

  const perm = await ensurePermission();
  if (perm === 'denied') {
    return {
      ok: false, statusCode: null, retriable: false, kind: 'consent_denied',
      detail: 'gather: pos=false (location permission denied)',
      positionObtained: false, geoMethod: APPDATA_GEO_METHOD, geoError: 'permission denied',
    };
  }

  let pos;
  try {
    pos = await Geolocation.getCurrentPosition({
      enableHighAccuracy: true,
      timeout: 30_000,
      maximumAge: 0,
    });
  } catch (e) {
    const msg = (e as Error)?.message ?? 'Location unavailable';
    const denied = /denied|permission/i.test(msg);
    return {
      ok: false,
      statusCode: null,
      retriable: !denied, // permission denial is terminal; a timeout/unavailable can retry once
      kind: denied ? 'consent_denied' : 'transient',
      detail: `gather: pos=false (getCurrentPosition failed: ${msg})`,
      positionObtained: false, geoMethod: APPDATA_GEO_METHOD, geoError: msg,
    };
  }

  const nowMs = Date.now();
  const acc = pos.coords.accuracy;
  // accuracy is mandatory and must be a real positive value. Do NOT coerce a
  // missing/zero accuracy to 0 (that would submit a meaningless fix); treat it
  // as an unusable position and let the caller retry the collector.
  if (typeof acc !== 'number' || !Number.isFinite(acc) || acc <= 0) {
    return {
      ok: false, statusCode: null, retriable: true, kind: 'transient',
      detail: `gather: pos=true but accuracy invalid (acc=${String(acc)}) - nothing sent`,
      positionObtained: false, geoMethod: APPDATA_GEO_METHOD, geoError: `invalid accuracy: ${String(acc)}`,
    };
  }

  const location: AppDataLocation = {
    latitude: pos.coords.latitude,
    longitude: pos.coords.longitude,
    accuracy: acc,
    timestamp: toLocanceTimestamp(pos.timestamp, nowMs),
  };

  const body = {
    appId,
    sessionId,
    clientCurrentTime: toLocanceTimestamp(nowMs, nowMs),
    locations: [location],
  };

  // We have a usable fix. Record WHAT we're sending (accuracy + the exact timestamp) so a
  // Locance "No latitude specified" / "Invalid latitude" is attributable: our own gather
  // vs Locance rejecting a valid position. Prefixes the beacon envelope.
  const gatherStr = `gather: pos=true acc=${acc} sentTs=${location.timestamp} locs=${body.locations.length}`;

  // POST via the NATIVE HTTP stack (CapacitorHttp), NOT the WKWebView fetch. The appData host
  // (lsl.sabotagefantasy.com) returns no CORS headers, so a cross-origin fetch from the
  // WebView origin (www.sabotagefantasy.com) is blocked at the preflight and rejects with
  // "Load failed" (no HTTP status) — the request never reaches Locance. A native URLSession
  // request is not subject to browser CORS (TLS on the host is ATS-compliant). Native-only,
  // called DIRECTLY — we do NOT enable the global CapacitorHttp fetch patch.
  let httpStatus: number;
  let rawBody: string;
  try {
    const response = await CapacitorHttp.post({
      url: APPDATA_URL,
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      data: body, // object; CapacitorHttp serializes it for the json content-type
      responseType: 'text', // hand us the exact raw body to capture + reparse ourselves
    });
    httpStatus = response.status;
    // With responseType 'text', data is the raw string. Guard the object case too (some
    // platforms auto-parse json) so we always have a string to parse + log.
    rawBody = typeof response.data === 'string' ? response.data : JSON.stringify(response.data ?? '');
  } catch (e) {
    return {
      ok: false, statusCode: null, retriable: true, kind: 'transient',
      detail: `${gatherStr} | native POST error: ${(e as Error)?.message ?? 'appData request failed'}`,
      positionObtained: true, geoMethod: APPDATA_GEO_METHOD, geoError: null,
    };
  }

  // Capture the FULL appData response — do NOT discard it. Per LS_API v4.23 sec 5.3 the
  // response carries a statusCode AND a statusMsg that NAMES the exact rejected field
  // ("Invalid sessionId" / "Invalid timestamp" / "No future timestamp allowed" /
  // "Invalid latitude" / "Invalid json POST data" ...). Parse defensively (statusCode may be
  // a number OR numeric string; the message may be statusMsg / status_msg / message / msg).
  // All of it is forwarded to the beacon.
  let parsed: Record<string, unknown> | null = null;
  try {
    parsed = JSON.parse(rawBody) as Record<string, unknown>;
  } catch {
    // non-JSON body: rawBody itself is the answer (captured in the envelope below).
  }
  const scRaw = parsed?.statusCode ?? parsed?.status_code ?? null;
  const statusCode =
    typeof scRaw === 'number' && Number.isFinite(scRaw) ? scRaw
      : typeof scRaw === 'string' && scRaw.trim() !== '' && Number.isFinite(Number(scRaw)) ? Number(scRaw)
        : null;
  const msgRaw = parsed?.statusMsg ?? parsed?.status_msg ?? parsed?.message ?? parsed?.msg ?? null;
  const statusMsg = typeof msgRaw === 'string' ? msgRaw : null;

  // Envelope for the beacon's error_message. http/statusCode/statusMsg FIRST (the answer),
  // so it survives the beacon fn's 300-char truncation; then the raw body for the non-JSON
  // or unnamed case. This is what the beacon reads out server-side.
  const envelope =
    `${gatherStr} | http=${httpStatus} statusCode=${statusCode ?? '?'} statusMsg=${statusMsg ?? '?'}` +
    (rawBody ? ` body=${rawBody.replace(/\s+/g, ' ').trim().slice(0, 400)}` : '');

  // Success = HTTP 2xx AND (no statusCode, or statusCode 0). A non-zero appData statusCode
  // (e.g. 48 "Invalid sessionId") means the submission was rejected; surface it so the
  // collector fails rather than referencing a session Locance never stored.
  const httpOk = httpStatus >= 200 && httpStatus < 300;
  const codeOk = statusCode === null || statusCode === 0;
  if (httpOk && codeOk) {
    return {
      ok: true, statusCode, retriable: false, detail: envelope,
      positionObtained: true, geoMethod: APPDATA_GEO_METHOD, geoError: null,
    };
  }
  return {
    ok: false,
    statusCode,
    retriable: httpOk, // a transport-level non-2xx can retry; an explicit reject code cannot
    kind: 'unavailable',
    detail: envelope,
    positionObtained: true, geoMethod: APPDATA_GEO_METHOD, geoError: null,
  };
}
